import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography
} from '@mui/material';
import {
  publicUndoLastScore,
  undoLastScore,
  undoPlayoffScore
} from '../../../features/game/gameSlice';
import { GameStatus } from '../../../constants/gameConstants';
import { useAuth } from '../../../contexts/authContext';
import { hasWorkspaceAccess } from '../../../helpers/access';

const UndoButton = ({ game, isPublic, portal }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [showUndoButton, setShowUndoButton] = useState(true);
  const [countdown, setCountdown] = useState(60);
  const undoing = useSelector((state) => state.game.undoingScore);
  const scoringGame = useSelector((state) => state.game.scoringGame);

  const { currentUser, currentUserProfile, currentWorkspace } = useAuth();

  // Calculate countdown from game.updatedAt - only for COMPLETE games.
  // Standalone quick games (no event) have no undo timer.
  useEffect(() => {
    if (isPublic && game.status === GameStatus.COMPLETE && game.updatedAt && game.event) {
      // Reset showUndoButton when updatedAt changes (new score added)
      setShowUndoButton(true);

      const updateTime = game.updatedAt.toDate ? game.updatedAt.toDate() : new Date(game.updatedAt);
      const oneMinuteLater = new Date(updateTime.getTime() + 300000); // Add 300 seconds

      const updateCountdown = () => {
        const now = new Date();
        const timeLeft = Math.max(0, Math.ceil((oneMinuteLater - now) / 1000));

        setCountdown(timeLeft);

        if (timeLeft <= 0) {
          setShowUndoButton(false);
        }
      };

      // Initial calculation
      updateCountdown();

      // Start interval to update every second
      const timer = setInterval(updateCountdown, 1000);
      return () => clearInterval(timer);
    }
  }, [isPublic, game.status, game.updatedAt, game.event]);

  const handleUndo = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleConfirmUndo = () => {
    if (portal) {
      Promise.resolve(portal.onUndo()).finally(() => setOpen(false));
      return;
    }

    if (game.status === GameStatus.PLAYOFF && game.playoff && !game.manualScore) {
      dispatch(undoPlayoffScore(game.id))
        .unwrap()
        .then(() => {
          setOpen(false);
          if (game.manualScore && game.event && game.section) {
            if (isPublic) {
              // navigate(`/public/scoring/${game.event}/${game.venue}/${game.green}${game.rink}`);

              navigate(-1);
            } else {
              navigate(`/events/${game.event}/section/${game.section}`);
            }
          }
        });
    } else {
      if (isPublic) {
        dispatch(publicUndoLastScore(game.id))
          .unwrap()
          .then(() => {
            setOpen(false);
            if (game.manualScore && game.event && game.section) {
              // navigate(`/public/scoring/${game.event}/${game.venue}/${game.green}${game.rink}`);

              navigate(-1);
            }
          });
      } else {
        dispatch(undoLastScore(game.id))
          .unwrap()
          .then(() => {
            setOpen(false);
            if (game.manualScore && game.event && game.section) {
              navigate(`/events/${game.event}/section/${game.section}`);
            }
          });
      }
    }
  };

  const canUndo = portal
    ? game.status !== GameStatus.NOT_STARTED
    : (currentUser &&
        (hasWorkspaceAccess(currentUserProfile, currentWorkspace, 'scorer') ||
          currentUser.uid === game.scorer?.id)) ||
      isPublic;

  return (
    (canUndo && (
      <>
        {!portal && game.status === GameStatus.COMPLETE && isPublic && !showUndoButton ? (
          <Typography variant="caption" color="textSecondary" sx={{ mb: 1, textAlign: 'center' }}>
            This game is complete. Please ask the administrator to undo the last score if needed.
          </Typography>
        ) : showUndoButton ? (
          <Button
            variant={'contained'}
            color={game.status === GameStatus.PLAYOFF && game.playoff ? 'warning' : 'primary'}
            elevation={2}
            sx={{ width: '100%', my: 1, fontWeight: 'bold' }}
            disabled={undoing || scoringGame || portal?.busy}
            onClick={() => handleUndo()}>
            {!portal && isPublic && game.status === GameStatus.COMPLETE && game.event
              ? `Undo (${countdown})`
              : 'Undo'}
          </Button>
        ) : (
          <Typography variant="caption" color="textSecondary" sx={{ mb: 1, textAlign: 'center' }}>
            This game is complete. Please ask the administrator to undo the last score if needed.
          </Typography>
        )}
        <Dialog
          open={open}
          onClose={handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description">
          <DialogTitle id="alert-dialog-title">Undo Last Score</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure you want to undo the last score? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            {undoing || portal?.busy ? (
              <CircularProgress />
            ) : (
              <>
                <Button onClick={handleClose} color="primary">
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmUndo}
                  color={'primary'}
                  autoFocus
                  disabled={undoing || portal?.busy}>
                  Undo
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>
      </>
    ))
  );
};

export default UndoButton;
